import React, { useState, useMemo, useRef } from "react";
import axios from "axios";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Config from "../../Config";
import tw from "tailwind-styled-components";
import { CombineInputGroup, FieldWrappers, InputGroup, Label, SubmitBtn } from "../../Components/Styles/InputStyles";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendar, FaSpinner } from "react-icons/fa";
import moment from "moment";
import GeneratedSalarySlip from "./GeneratedSalarySlip";
// ---------- helpers ----------

const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
];

const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
];

function twoDigits(n) {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
}

function threeDigits(n) {
    const h = Math.floor(n / 100);
    const rest = n % 100;
    return (h ? ones[h] + " Hundred" + (rest ? " " : "") : "") + (rest ? twoDigits(rest) : "");
}

// Indian numbering system: crore / lakh / thousand / hundred
function numberToWordsINR(num) {
    const n = Math.round(Number(num) || 0);
    if (n === 0) return "Zero Rupees Only";
    let rupees = n;
    const crore = Math.floor(rupees / 10000000);
    rupees %= 10000000;
    const lakh = Math.floor(rupees / 100000);
    rupees %= 100000;
    const thousand = Math.floor(rupees / 1000);
    rupees %= 1000;
    const hundred = rupees;

    const parts = [];
    if (crore) parts.push(threeDigits(crore) + " Crore");
    if (lakh) parts.push(threeDigits(lakh) + " Lakh");
    if (thousand) parts.push(threeDigits(thousand) + " Thousand");
    if (hundred) parts.push(threeDigits(hundred));

    return parts.join(" ").replace(/\s+/g, " ").trim() + " Rupees Only";
}

const fmt = (n) =>
    (Number(n) || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
    });

// ---------- field primitives ----------

function SectionHeading({ index, title }) {
    return (
        <div className="mb-4 flex items-center gap-2 border-l-4 border-[#2b6cb0] bg-[#ebf8ff] px-3 py-2">
            <span className="text-[10px] font-bold tracking-wide text-black">
                {index}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-wide text-black">
                {title}
            </span>
        </div>
    );
}

const getErrorBorder = (touched, error) => {
    return touched && error
        ? "border border-red-500"
        : "border border-gray-300";
};



const fmtInput = (value) => {
    if (!value) return "";
    return new Intl.NumberFormat("en-IN").format(value);
};

// ---------- main form ----------

export default function SalarySlip() {
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    const [slipData, setSlipData] = useState(null);
    const [isGeneratingSlip, setIsGeneratingSlip] = useState(false);
    const [payDateCalendarOpen, setPayDateCalendarOpen] = useState(false);
    const [payPeriodCalendarOpen, setPayPeriodCalendarOpen] = useState(false);
    const [earnings, setEarnings] = useState([
        {
            description: "Basic Salary",
            amount: "",
        },
        {
            description: "House Rent Allowance",
            amount: "",
        },
        {
            description: "",
            amount: "",
        },
    ]);

    const [deductions, setDeductions] = useState([
        {
            description: "Provident Fund",
            amount: "",
        },
        {
            description: "Income Tax",
            amount: "",
        },
        {
            description: "",
            amount: "",
        },
    ]);

    const updateEarning = (index, field, value) => {
        setEarnings((prev) =>
            prev.map((item, i) =>
                i === index
                    ? { ...item, [field]: value }
                    : item
            )
        );
    };

    const updateDeduction = (index, field, value) => {
        setDeductions((prev) =>
            prev.map((item, i) =>
                i === index
                    ? { ...item, [field]: value }
                    : item
            )
        );
    };

    //---Create slip---------------------------    
    const addSalarySlipFunction = async (payload) => {
        const response = await axios.post(
            `${Config.apiUrl}/salary-slip/add`,
            payload,
            {
                headers: {
                    // Authorization: `Bearer ${user?.token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response;
    };

    const {
        mutate: addSalarySlipMutate,
        isLoading: isSalarySlipLoading,
    } = useMutation(addSalarySlipFunction, {
        onSuccess: (res) => {
            toast.success(
                res?.data?.msg || "Salary slip generated successfully"
            );
        },

        onError: (err) => {
            toast.error(
                err?.response?.data?.msg ||
                err?.message ||
                "Something went wrong"
            );
        },
    });


    const initialValues = {
        companyName: "",
        city: "",
        pincode: "",
        address: "",

        employeeName: "",
        employeeId: "",
        payPeriod: "",
        paidDays: "",
        lossOfPayDays: "",
        payDate: moment().format("YYYY-MM-DD"),
    };

    const validationSchema = Yup.object({
        companyName: Yup.string().required("Company name is required"),
        address: Yup.string().required("Address is required"),
        city: Yup.string()
            .matches(/^[A-Za-z\s]+$/, "City can contain only letters")
            .required("City is required"),

        pincode: Yup.string()
            .matches(/^\d{6}$/, "Pincode must be exactly 6 digits")
            .required("Pincode is required"),


        employeeName: Yup.string().required("Employee name is required"),
        payPeriod: Yup.string().required("Pay period is required"),

        paidDays: Yup.number()
            .transform((value, originalValue) =>
                originalValue === "" ? undefined : value
            )
            .typeError("Enter a valid number")
            .required("Paid days is required"),

        lossOfPayDays: Yup.number()
            .transform((value, originalValue) =>
                originalValue === "" ? undefined : value
            )
            .typeError("Enter a valid number")
            .required("Loss of pay days is required"),
        employeeId: Yup.string()
            .required("Employee ID is required"),

        payDate: Yup.string().required("Pay date is required"),
    });


    // const handleSubmit = (values) => {
    //     const payload = {
    //         companyName: values.companyName,
    //         city: values.city,
    //         pincode: values.pincode,
    //         address: values.address,

    //         employeeName: values.employeeName,
    //         employeeId: values.employeeId,
    //         payPeriod: values.payPeriod,
    //         paidDays: Number(values.paidDays || 0),
    //         lossOfPayDays: Number(values.lossOfPayDays || 0),
    //         payDate: values.payDate,

    //         earnings: earnings.map((item) => ({
    //             description: item.description,
    //             amount: Number(item.amount || 0),
    //         })),

    //         deductions: deductions.map((item) => ({
    //             description: item.description,
    //             amount: Number(item.amount || 0),
    //         })),

    //         grossEarnings: computedTotals.grossEarnings,
    //         totalDeductions: computedTotals.totalDeductions,
    //         netSalary: computedTotals.totalNetPayable,
    //         amountInWords: computedTotals.amountInWords,
    //     };

    //     // console.log("📦 Salary Slip Payload:", payload);

    //     addSalarySlipMutate(payload);
    // };

    const handleSubmit = async (values) => {
    setIsGeneratingSlip(true);

    const payload = {
        companyName: values.companyName,
        city: values.city,
        pincode: values.pincode,
        address: values.address,

        employeeName: values.employeeName,
        employeeId: values.employeeId,
        payPeriod: values.payPeriod,
        paidDays: Number(values.paidDays || 0),
        lossOfPayDays: Number(values.lossOfPayDays || 0),
        payDate: values.payDate,

        earnings: earnings.map((item) => ({
            description: item.description,
            amount: Number(item.amount || 0),
        })),

        deductions: deductions.map((item) => ({
            description: item.description,
            amount: Number(item.amount || 0),
        })),

        grossEarnings: computedTotals.grossEarnings,
        totalDeductions: computedTotals.totalDeductions,
        netSalary: computedTotals.totalNetPayable,
        amountInWords: computedTotals.amountInWords,
    };

    // Wait 1.5 seconds before generating the slip
    await new Promise((resolve) => setTimeout(resolve, 1500));

    navigate("/salary-slip/generated", {
        state: {
            slipData: payload,
        },
    });
};

    // Calculation memo update
    const computedTotals = useMemo(() => {
        const grossEarnings = earnings.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
        const totalDeductions = deductions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
        const totalNetPayable = grossEarnings - totalDeductions;

        return {
            grossEarnings,
            totalDeductions,
            totalNetPayable: totalNetPayable > 0 ? totalNetPayable : 0,
            amountInWords: numberToWordsINR(totalNetPayable > 0 ? totalNetPayable : 0)
        };
    }, [earnings, deductions]);

    return (
        <>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
                <Form
                    className="min-h-full bg-[#F7F8FA] px-4 py-10">
                    <div className="mx-auto max-w-[900px]">
                        {/* header */}
                        <div className="mb-8 flex items-start justify-between">
                            <div>
                                <div className="text-xl  text-black font-semibold tracking-wider">
                                    Generate Salary Slip
                                </div>
                                <p className="mt-1 text-[13px] text-slate-500">
                                    Create a salary slip with employee, earnings, deductions and payroll information.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-5">
                            {/* Section 1 */}
                            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <SectionHeading index="1" title="Company Details" />
                                <CombineInputGroup>
                                    <InputGroup>
                                        <Label htmlFor="companyName">Company Name *</Label>
                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.companyName,
                                                errors.companyName
                                            )}
                                        >
                                            <Field
                                                id="companyName"
                                                name="companyName"
                                                type="text"
                                                placeholder="Enter company name"
                                            />
                                        </FieldWrappers>

                                        {/* {touched.companyName && errors.companyName && (
                                            <ErrorText>{errors.companyName}</ErrorText>
                                        )} */}
                                    </InputGroup>

                                </CombineInputGroup>

                                <CombineInputGroup className="mt-2">
                                    <InputGroup>
                                        <Label htmlFor="city">City *</Label>
                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.city,
                                                errors.city
                                            )}
                                        >
                                            <Field
                                                id="city"
                                                name="city"
                                                type="text"
                                                placeholder="Enter city"
                                                onKeyDown={(e) => {
                                                    // Allow letters, space and control keys
                                                    if (
                                                        !/[a-zA-Z\s]/.test(e.key) &&
                                                        ![
                                                            "Backspace",
                                                            "Delete",
                                                            "ArrowLeft",
                                                            "ArrowRight",
                                                            "Tab",
                                                        ].includes(e.key)
                                                    ) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    // Only letters and spaces
                                                    if (/^[A-Za-z\s]*$/.test(value)) {
                                                        setFieldValue("city", value);
                                                    }
                                                }}
                                                onBlur={() => setFieldTouched("city", true)}
                                            />
                                        </FieldWrappers>
                                        {/* {touched.city && errors.city && (
                                            <ErrorText>{errors.city}</ErrorText>
                                        )} */}
                                    </InputGroup>

                                    <InputGroup>
                                        <Label htmlFor="pincode">Pincode *</Label>
                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.pincode,
                                                errors.pincode
                                            )}
                                        >
                                            <Field
                                                id="pincode"
                                                name="pincode"
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={6}
                                                placeholder="Enter pincode"
                                                onKeyDown={(e) => {
                                                    // Allow only numbers and control keys
                                                    if (
                                                        !/[0-9]/.test(e.key) &&
                                                        ![
                                                            "Backspace",
                                                            "Delete",
                                                            "ArrowLeft",
                                                            "ArrowRight",
                                                            "Tab",
                                                        ].includes(e.key)
                                                    ) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    // Only numbers and maximum 6 digits
                                                    if (/^\d{0,6}$/.test(value)) {
                                                        setFieldValue("pincode", value);
                                                    }
                                                }}
                                                onBlur={() => setFieldTouched("pincode", true)}
                                            />
                                        </FieldWrappers>
                                        {/* {touched.pincode && errors.pincode && (
                                            <ErrorText>{errors.pincode}</ErrorText>
                                        )} */}
                                    </InputGroup>


                                </CombineInputGroup>

                                <div className="mt-2">
                                    <InputGroup>
                                        <Label htmlFor="address">Address *</Label>

                                        <Field
                                            as={Textarea}
                                            id="address"
                                            name="address"
                                            rows={3}
                                            placeholder="Enter address"
                                        />

                                        {touched.address && errors.address && (
                                            <ErrorText>{errors.address}</ErrorText>
                                        )}
                                    </InputGroup>
                                </div>
                            </div>

                            {/* Section 2 */}
                            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <SectionHeading index="2" title="Employee Details" />

                                <CombineInputGroup>
                                    <InputGroup>
                                        <Label htmlFor="employeeName">Employee Name *</Label>
                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.employeeName,
                                                errors.employeeName
                                            )}
                                        >
                                            <Field
                                                id="employeeName"
                                                name="employeeName"
                                                type="text"
                                                placeholder="Enter employee name"
                                            />
                                        </FieldWrappers>

                                        {/* {touched.employeeName && errors.employeeName && (
                                            <ErrorText>{errors.employeeName}</ErrorText>
                                        )} */}
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="employeeId">Employee ID *</Label>
                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.employeeId,
                                                errors.employeeId
                                            )}
                                        >
                                           <Field
                                                id="employeeId"
                                                name="employeeId"
                                                placeholder="Enter employee id"
                                                onKeyDown={(e) => {
                                                    if (e.key === " ") {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\s/g, "");
                                                    setFieldValue("employeeId", value);
                                                }}
                                            />
                                        </FieldWrappers>
                                        {/* {touched.employeeId && errors.employeeId && (
                                            <ErrorText>{errors.employeeId}</ErrorText>
                                        )} */}
                                    </InputGroup>


                                </CombineInputGroup>

                                <CombineInputGroup className="mt-2">
                                    <InputGroup>
                                        <Label htmlFor="payPeriod">Pay Period *</Label>
                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.payPeriod,
                                                errors.payPeriod
                                            )}
                                        >
                                            <DatePicker
                                                selected={
                                                    values.payPeriod
                                                        ? new Date(`${values.payPeriod}-01`)
                                                        : null
                                                }
                                                onChange={(date) => {
                                                    if (!date) return;

                                                    setFieldValue(
                                                        "payPeriod",
                                                        moment(date).format("YYYY-MM")
                                                    );

                                                    setPayPeriodCalendarOpen(false);
                                                }}
                                                onClickOutside={() => setPayPeriodCalendarOpen(false)}
                                                open={payPeriodCalendarOpen}
                                                onInputClick={() => setPayPeriodCalendarOpen(true)}
                                                dateFormat="MMMM yyyy"
                                                showMonthYearPicker
                                                // showFullMonthYearPicker
                                                showTwoColumnMonthYearPicker
                                                placeholderText="Select pay period"
                                                className="w-full cursor-pointer bg-transparent outline-none"
                                                showIcon
                                                icon={
                                                    <FaRegCalendar className="mt-0.5 text-xs text-gray-400" />
                                                }
                                                showPopperArrow={false}
                                                onFocus={(e) => e.target.blur()}
                                                wrapperClassName="w-full"
                                                portalId="root-portal"
                                                popperClassName="react-datepicker-popper"
                                            />
                                        </FieldWrappers>

                                        {/* {touched.payPeriod && errors.payPeriod && (
                                            <ErrorText>{errors.payPeriod}</ErrorText>
                                        )} */}
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="paidDays">Paid Days *</Label>

                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.paidDays,
                                                errors.paidDays
                                            )}
                                        >
                                            <Field
                                                id="paidDays"
                                                name="paidDays"
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={2}
                                                placeholder="Enter paid days"
                                                onKeyDown={(e) => {
                                                    // Allow only numbers and control keys
                                                    if (
                                                        !/[0-9]/.test(e.key) &&
                                                        ![
                                                            "Backspace",
                                                            "Delete",
                                                            "ArrowLeft",
                                                            "ArrowRight",
                                                            "Tab",
                                                        ].includes(e.key)
                                                    ) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    // Only numbers
                                                    if (!/^\d*$/.test(value)) {
                                                        return;
                                                    }

                                                    // Maximum 31
                                                    if (value === "" || Number(value) <= 31) {
                                                        setFieldValue("paidDays", value);
                                                    }
                                                }}
                                                onBlur={() => setFieldTouched("paidDays", true)}
                                            />
                                        </FieldWrappers>

                                        {/* {touched.paidDays && errors.paidDays && (
                                            <ErrorText>{errors.paidDays}</ErrorText>
                                        )} */}
                                    </InputGroup>


                                </CombineInputGroup>

                                <CombineInputGroup className="mt-2">
                                    <InputGroup>
                                        <Label htmlFor="lossOfPayDays">Loss of Pay Days *</Label>

                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.lossOfPayDays,
                                                errors.lossOfPayDays
                                            )}
                                        >
                                            <Field
                                                id="lossOfPayDays"
                                                name="lossOfPayDays"
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={2}
                                                placeholder="Enter loss of pay days"
                                                onKeyDown={(e) => {
                                                    // Allow only numbers and control keys
                                                    if (
                                                        !/[0-9]/.test(e.key) &&
                                                        ![
                                                            "Backspace",
                                                            "Delete",
                                                            "ArrowLeft",
                                                            "ArrowRight",
                                                            "Tab",
                                                        ].includes(e.key)
                                                    ) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    // Only numbers
                                                    if (!/^\d*$/.test(value)) {
                                                        return;
                                                    }

                                                    // Maximum 31
                                                    if (value === "" || Number(value) <= 31) {
                                                        setFieldValue("lossOfPayDays", value);
                                                    }
                                                }}
                                                onBlur={() => setFieldTouched("lossOfPayDays", true)}
                                            />
                                        </FieldWrappers>

                                        {/* {touched.lossOfPayDays && errors.lossOfPayDays && (
                                            <ErrorText>{errors.lossOfPayDays}</ErrorText>
                                        )} */}
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="payDate">Pay Date *</Label>

                                        <FieldWrappers
                                            className="relative cursor-pointer items-center h-11"
                                        >
                                            <DatePicker
                                                selected={
                                                    values.payDate
                                                        ? new Date(values.payDate)
                                                        : null
                                                }
                                                onChange={(date) => {
                                                    if (!date) return;

                                                    setFieldValue(
                                                        "payDate",
                                                        moment(date).format("YYYY-MM-DD")
                                                    );

                                                    setPayDateCalendarOpen(false);
                                                }}
                                                onClickOutside={() =>
                                                    setPayDateCalendarOpen(false)
                                                }
                                                onSelect={() =>
                                                    setPayDateCalendarOpen(false)
                                                }
                                                open={payDateCalendarOpen}
                                                onInputClick={() =>
                                                    setPayDateCalendarOpen(true)
                                                }
                                                dateFormat="yyyy-MM-dd"
                                                placeholderText="Select pay date"
                                                className="w-full cursor-pointer bg-transparent outline-none"
                                                showIcon
                                                icon={
                                                    <FaRegCalendar className="mt-0.5 text-xs text-gray-400" />
                                                }
                                                showPopperArrow={false}
                                                onFocus={(e) => e.target.blur()}
                                                wrapperClassName="w-full"
                                                portalId="root-portal"
                                                popperClassName="react-datepicker-popper"
                                            />
                                        </FieldWrappers>

                                        {touched.payDate && errors.payDate && (
                                            <ErrorText>
                                                {errors.payDate}
                                            </ErrorText>
                                        )}
                                    </InputGroup>


                                </CombineInputGroup>
                            </div>

                            {/* Section 3 */}
                            {/* <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <SectionHeading index="3" title="Income Details" />
                                
                            </div> */}

                            {/* ===================== SECTION 3 ===================== */}
                            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <SectionHeading index="3" title="Income & Deductions" />

                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

                                    {/* ===================== EARNINGS ===================== */}
                                    <div>
                                        <div className="mb-3">
                                            <h3 className="text-sm font-semibold text-slate-800">
                                                Income
                                            </h3>
                                            <p className="mt-0.5 text-xs text-slate-500">
                                                Salary components paid to the employee
                                            </p>
                                        </div>

                                        <div className="overflow-hidden rounded-lg border border-slate-200">
                                            <table className="w-full text-sm">
                                                <thead className="bg-slate-50">
                                                    <tr>
                                                        <th className="p-3 text-left font-semibold text-slate-600">
                                                            Income
                                                        </th>

                                                        <th className="w-36 p-3 text-right font-semibold text-slate-600">
                                                            Amount
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody className="divide-y divide-slate-200">
                                                    {earnings.map((item, index) => (
                                                        <tr key={index}>
                                                            {/* <td className="p-3">
                                                                <div className="px-1 py-2 text-sm font-medium text-slate-700">
                                                                    {item.description}
                                                                </div>
                                                            </td> */}
                                                            <td className="p-3">
                                                                {index === 2 ? (
                                                                    <FieldWrappers>
                                                                        <input
                                                                            type="text"
                                                                            className="w-full bg-transparent outline-none"
                                                                            placeholder="Enter income name"
                                                                            value={item.description}
                                                                            onChange={(e) => {
                                                                                const description = e.target.value;

                                                                                updateEarning(index, "description", description);

                                                                                // Clear amount when description is removed
                                                                                if (!description.trim()) {
                                                                                    updateEarning(index, "amount", "");
                                                                                }
                                                                            }}
                                                                        />
                                                                    </FieldWrappers>
                                                                ) : (
                                                                    <div className="px-1 py-2 text-sm font-medium text-slate-700">
                                                                        {item.description}
                                                                    </div>
                                                                )}
                                                            </td>

                                                            <td className="p-3">
                                                                <FieldWrappers
                                                                    className={`${index === 2 && !item.description.trim()
                                                                            ? "bg-gray-100 opacity-60 cursor-not-allowed"
                                                                            : ""
                                                                        }`}
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        inputMode="numeric"
                                                                        className={`w-full bg-transparent text-left outline-none ${index === 2 && !item.description.trim()
                                                                                ? "cursor-not-allowed text-gray-400"
                                                                                : ""
                                                                            }`}
                                                                        placeholder="Amount"
                                                                        value={fmtInput(item.amount)}
                                                                        disabled={index === 2 && !item.description.trim()}
                                                                        onChange={(e) => {
                                                                            const rawValue = e.target.value.replace(/,/g, "");

                                                                            if (rawValue === "" || /^\d+$/.test(rawValue)) {
                                                                                const amount = Number(rawValue);

                                                                                if (amount <= 1000000 || rawValue === "") {
                                                                                    updateEarning(
                                                                                        index,
                                                                                        "amount",
                                                                                        rawValue
                                                                                    );
                                                                                } else {
                                                                                    toast.error(
                                                                                        "Amount cannot be greater than ₹10,00,000"
                                                                                    );
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                </FieldWrappers>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Total Earnings */}
                                        <div className="mt-3 flex justify-end">
                                            <div className="flex w-52 justify-between border-t pt-3 text-sm font-semibold">
                                                <span>Total Income</span>

                                                <span>
                                                    ₹ {fmt(computedTotals.grossEarnings)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>


                                    {/* ===================== DEDUCTIONS ===================== */}
                                    <div>
                                        <div className="mb-3">
                                            <h3 className="text-sm font-semibold text-slate-800">
                                                Deductions
                                            </h3>

                                            <p className="mt-0.5 text-xs text-slate-500">
                                                Applicable deductions from salary
                                            </p>
                                        </div>

                                        <div className="overflow-hidden rounded-lg border border-slate-200">
                                            <table className="w-full text-sm">
                                                <thead className="bg-slate-50">
                                                    <tr>
                                                        <th className="p-3 text-left font-semibold text-slate-600">
                                                            Deductions
                                                        </th>

                                                        <th className="w-36 p-3 text-right font-semibold text-slate-600">
                                                            Amount
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody className="divide-y divide-slate-200">
                                                    {deductions.map((item, index) => (
                                                        <tr key={index}>
                                                            {/* <td className="p-3">
                                                                <div className="px-1 py-2 text-sm font-medium text-slate-700">
                                                                    {item.description}
                                                                </div>
                                                            </td> */}
                                                            <td className="p-3">
                                                                {index === 2 ? (
                                                                    <FieldWrappers>
                                                                        <input
                                                                            type="text"
                                                                            className="w-full bg-transparent outline-none"
                                                                            placeholder="Enter deduction name"
                                                                            value={item.description}
                                                                            onChange={(e) => {
                                                                                const description = e.target.value;

                                                                                updateDeduction(
                                                                                    index,
                                                                                    "description",
                                                                                    description
                                                                                );

                                                                                if (!description.trim()) {
                                                                                    updateDeduction(index, "amount", "");
                                                                                }
                                                                            }}
                                                                        />
                                                                    </FieldWrappers>
                                                                ) : (
                                                                    <div className="px-1 py-2 text-sm font-medium text-slate-700">
                                                                        {item.description}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="p-3">
                                                                <FieldWrappers
                                                                    className={`${index === 2 && !item.description.trim()
                                                                            ? "bg-gray-100 opacity-60 cursor-not-allowed"
                                                                            : ""
                                                                        }`}
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        inputMode="numeric"
                                                                        className={`w-full bg-transparent text-left outline-none ${index === 2 && !item.description.trim()
                                                                                ? "cursor-not-allowed text-gray-400"
                                                                                : ""
                                                                            }`}
                                                                        placeholder="Amount"
                                                                        value={fmtInput(item.amount)}
                                                                        disabled={index === 2 && !item.description.trim()}
                                                                        onChange={(e) => {
                                                                            const rawValue = e.target.value.replace(/,/g, "");

                                                                            if (rawValue === "" || /^\d+$/.test(rawValue)) {
                                                                                const amount = Number(rawValue);

                                                                                if (amount <= 1000000 || rawValue === "") {
                                                                                    updateDeduction(
                                                                                        index,
                                                                                        "amount",
                                                                                        rawValue
                                                                                    );
                                                                                } else {
                                                                                    toast.error(
                                                                                        "Amount cannot be greater than ₹10,00,000"
                                                                                    );
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                </FieldWrappers>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Total Deductions */}
                                        <div className="mt-3 flex justify-end">
                                            <div className="flex w-52 justify-between border-t pt-3 text-sm font-semibold">
                                                <span>Total Deductions</span>

                                                <span>
                                                    ₹ {fmt(computedTotals.totalDeductions)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                </div>


                                {/* ===================== NET SALARY ===================== */}
                                <div className="mt-6 flex justify-end">
                                    <div className="w-80 rounded-lg bg-slate-50 p-4">

                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-600">
                                                Gross Income
                                            </span>

                                            <span className="font-medium">
                                                ₹ {fmt(computedTotals.grossEarnings)}
                                            </span>
                                        </div>

                                        <div className="mt-2 flex justify-between text-sm">
                                            <span className="text-slate-600">
                                                Total Deductions
                                            </span>

                                            <span className="font-medium">
                                                ₹ {fmt(computedTotals.totalDeductions)}
                                            </span>
                                        </div>

                                        <div className="mt-3 flex justify-between border-t pt-3 text-lg font-bold text-[#1A365D]">
                                            <span>Net Salary</span>

                                            <span>
                                                ₹ {fmt(computedTotals.totalNetPayable)}
                                            </span>
                                        </div>

                                        <div className="mt-2 border-t border-dashed pt-2 text-right text-xs text-slate-500">
                                            {computedTotals.amountInWords}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* footer action */}
                        <div className="mt-8 flex justify-end">

                            {/* <SubmitBtn
                                type="submit"
                                disabled={isSalarySlipLoading}
                            >
                                {isSalarySlipLoading ? "Generating..." : "Generate Slip"}
                            </SubmitBtn> */}
                            <SubmitBtn
                                type="submit"
                                disabled={isGeneratingSlip}
                            >
                                {isGeneratingSlip ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <FaSpinner className="animate-spin text-sm" />
                                        Generating...
                                    </span>
                                ) : (
                                    "Generate Slip"
                                )}
                            </SubmitBtn>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
        </>

        
    );
}

const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;
const FieldWrapper = tw.div`w-full`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;









































// import React, { useState, useMemo, useRef } from "react";
// import axios from "axios";
// import { useMutation, useQuery } from "react-query";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import Config from "../../Config";
// import tw from "tailwind-styled-components";
// import { CombineInputGroup, FieldWrappers, InputGroup, Label, SubmitBtn } from "../../Components/Styles/InputStyles";
// import { Formik, Form, Field } from "formik";
// import * as Yup from "yup";
// import { useNavigate } from "react-router-dom";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import { FaRegCalendar } from "react-icons/fa";
// import moment from "moment";
// // ---------- helpers ----------

// const ones = [
//     "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
//     "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
//     "Seventeen", "Eighteen", "Nineteen",
// ];

// const tens = [
//     "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
// ];

// function twoDigits(n) {
//     if (n < 20) return ones[n];
//     return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
// }

// function threeDigits(n) {
//     const h = Math.floor(n / 100);
//     const rest = n % 100;
//     return (h ? ones[h] + " Hundred" + (rest ? " " : "") : "") + (rest ? twoDigits(rest) : "");
// }

// // Indian numbering system: crore / lakh / thousand / hundred
// function numberToWordsINR(num) {
//     const n = Math.round(Number(num) || 0);
//     if (n === 0) return "Zero Rupees Only";
//     let rupees = n;
//     const crore = Math.floor(rupees / 10000000);
//     rupees %= 10000000;
//     const lakh = Math.floor(rupees / 100000);
//     rupees %= 100000;
//     const thousand = Math.floor(rupees / 1000);
//     rupees %= 1000;
//     const hundred = rupees;

//     const parts = [];
//     if (crore) parts.push(threeDigits(crore) + " Crore");
//     if (lakh) parts.push(threeDigits(lakh) + " Lakh");
//     if (thousand) parts.push(threeDigits(thousand) + " Thousand");
//     if (hundred) parts.push(threeDigits(hundred));

//     return parts.join(" ").replace(/\s+/g, " ").trim() + " Rupees Only";
// }

// const fmt = (n) =>
//     (Number(n) || 0).toLocaleString("en-IN", {
//         maximumFractionDigits: 2,
//     });

// // ---------- field primitives ----------

// function SectionHeading({ index, title }) {
//     return (
//         <div className="mb-4 flex items-center gap-2 border-l-4 border-[#2b6cb0] bg-[#ebf8ff] px-3 py-2">
//             <span className="text-[10px] font-bold tracking-wide text-black">
//                 {index}
//             </span>
//             <span className="text-[11px] font-bold uppercase tracking-wide text-black">
//                 {title}
//             </span>
//         </div>
//     );
// }

// const getErrorBorder = (touched, error) => {
//     return touched && error
//         ? "border border-red-500"
//         : "border border-gray-300";
// };



// const fmtInput = (value) => {
//     if (!value) return "";
//     return new Intl.NumberFormat("en-IN").format(value);
// };

// // ---------- main form ----------

// export default function SalarySlip() {
//     const navigate = useNavigate();
//     const user = useSelector((state) => state.UserReducer.user);
//     const [payDateCalendarOpen, setPayDateCalendarOpen] = useState(false);
//     const [payPeriodCalendarOpen, setPayPeriodCalendarOpen] = useState(false);
//     const [earnings, setEarnings] = useState([
//         {
//             description: "Basic Salary",
//             amount: "",
//         },
//         {
//             description: "House Rent Allowance",
//             amount: "",
//         },
//         {
//             description: "",
//             amount: "",
//         },
//     ]);

//     const [deductions, setDeductions] = useState([
//         {
//             description: "Provident Fund",
//             amount: "",
//         },
//         {
//             description: "Income Tax",
//             amount: "",
//         },
//         {
//             description: "",
//             amount: "",
//         },
//     ]);

//     const updateEarning = (index, field, value) => {
//         setEarnings((prev) =>
//             prev.map((item, i) =>
//                 i === index
//                     ? { ...item, [field]: value }
//                     : item
//             )
//         );
//     };

//     const updateDeduction = (index, field, value) => {
//         setDeductions((prev) =>
//             prev.map((item, i) =>
//                 i === index
//                     ? { ...item, [field]: value }
//                     : item
//             )
//         );
//     };

//     //---Create slip---------------------------    
//     const addSalarySlipFunction = async (payload) => {
//         const response = await axios.post(
//             `${Config.apiUrl}/salary-slip/add`,
//             payload,
//             {
//                 headers: {
//                     // Authorization: `Bearer ${user?.token}`,
//                     "Content-Type": "application/json",
//                 },
//             }
//         );
//         return response;
//     };

//     const {
//         mutate: addSalarySlipMutate,
//         isLoading: isSalarySlipLoading,
//     } = useMutation(addSalarySlipFunction, {
//         onSuccess: (res) => {
//             toast.success(
//                 res?.data?.msg || "Salary slip generated successfully"
//             );
//         },

//         onError: (err) => {
//             toast.error(
//                 err?.response?.data?.msg ||
//                 err?.message ||
//                 "Something went wrong"
//             );
//         },
//     });


//     const initialValues = {
//         companyName: "",
//         city: "",
//         pincode: "",
//         address: "",

//         employeeName: "",
//         employeeId: "",
//         payPeriod: "",
//         paidDays: "",
//         lossOfPayDays: "",
//         payDate: moment().format("YYYY-MM-DD"),
//     };

//     const validationSchema = Yup.object({
//         companyName: Yup.string().required("Company name is required"),
//         address: Yup.string().required("Address is required"),
//         city: Yup.string()
//             .matches(/^[A-Za-z\s]+$/, "City can contain only letters")
//             .required("City is required"),

//         pincode: Yup.string()
//             .matches(/^\d{6}$/, "Pincode must be exactly 6 digits")
//             .required("Pincode is required"),


//         employeeName: Yup.string().required("Employee name is required"),
//         payPeriod: Yup.string().required("Pay period is required"),

//         paidDays: Yup.number()
//             .transform((value, originalValue) =>
//                 originalValue === "" ? undefined : value
//             )
//             .typeError("Enter a valid number")
//             .required("Paid days is required"),

//         lossOfPayDays: Yup.number()
//             .transform((value, originalValue) =>
//                 originalValue === "" ? undefined : value
//             )
//             .typeError("Enter a valid number")
//             .required("Loss of pay days is required"),
//         employeeId: Yup.string()
//             .required("Employee ID is required"),

//         payDate: Yup.string().required("Pay date is required"),
//     });


//     const handleSubmit = (values) => {
//         const payload = {
//             companyName: values.companyName,
//             city: values.city,
//             pincode: values.pincode,
//             address: values.address,

//             employeeName: values.employeeName,
//             employeeId: values.employeeId,
//             payPeriod: values.payPeriod,
//             paidDays: Number(values.paidDays || 0),
//             lossOfPayDays: Number(values.lossOfPayDays || 0),
//             payDate: values.payDate,

//             earnings: earnings.map((item) => ({
//                 description: item.description,
//                 amount: Number(item.amount || 0),
//             })),

//             deductions: deductions.map((item) => ({
//                 description: item.description,
//                 amount: Number(item.amount || 0),
//             })),

//             grossEarnings: computedTotals.grossEarnings,
//             totalDeductions: computedTotals.totalDeductions,
//             netSalary: computedTotals.totalNetPayable,
//             amountInWords: computedTotals.amountInWords,
//         };

//         // console.log("📦 Salary Slip Payload:", payload);

//         addSalarySlipMutate(payload);
//     };

//     // Calculation memo update
//     const computedTotals = useMemo(() => {
//         const grossEarnings = earnings.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
//         const totalDeductions = deductions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
//         const totalNetPayable = grossEarnings - totalDeductions;

//         return {
//             grossEarnings,
//             totalDeductions,
//             totalNetPayable: totalNetPayable > 0 ? totalNetPayable : 0,
//             amountInWords: numberToWordsINR(totalNetPayable > 0 ? totalNetPayable : 0)
//         };
//     }, [earnings, deductions]);

//     return (
//         <Formik
//             initialValues={initialValues}
//             validationSchema={validationSchema}
//             onSubmit={handleSubmit}
//         >
//             {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
//                 <Form
//                     className="min-h-full bg-[#F7F8FA] px-4 py-10">
//                     <div className="mx-auto max-w-[900px]">
//                         {/* header */}
//                         <div className="mb-8 flex items-start justify-between">
//                             <div>
//                                 <div className="text-xl  text-black font-semibold tracking-wider">
//                                     Generate Salary Slip
//                                 </div>
//                                 <p className="mt-1 text-[13px] text-slate-500">
//                                     Create a salary slip with employee, earnings, deductions and payroll information.
//                                 </p>
//                             </div>
//                         </div>

//                         <div className="space-y-5">
//                             {/* Section 1 */}
//                             <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
//                                 <SectionHeading index="1" title="Company Details" />
//                                 <CombineInputGroup>
//                                     <InputGroup>
//                                         <Label htmlFor="companyName">Company Name *</Label>
//                                         <FieldWrappers
//                                             className={getErrorBorder(
//                                                 touched.companyName,
//                                                 errors.companyName
//                                             )}
//                                         >
//                                             <Field
//                                                 id="companyName"
//                                                 name="companyName"
//                                                 type="text"
//                                                 placeholder="Enter company name"
//                                             />
//                                         </FieldWrappers>

//                                         {/* {touched.companyName && errors.companyName && (
//                                             <ErrorText>{errors.companyName}</ErrorText>
//                                         )} */}
//                                     </InputGroup>

//                                 </CombineInputGroup>

//                                 <CombineInputGroup className="mt-2">
//                                     <InputGroup>
//                                         <Label htmlFor="city">City *</Label>
//                                         <FieldWrappers
//                                             className={getErrorBorder(
//                                                 touched.city,
//                                                 errors.city
//                                             )}
//                                         >
//                                             <Field
//                                                 id="city"
//                                                 name="city"
//                                                 type="text"
//                                                 placeholder="Enter city"
//                                                 onKeyDown={(e) => {
//                                                     // Allow letters, space and control keys
//                                                     if (
//                                                         !/[a-zA-Z\s]/.test(e.key) &&
//                                                         ![
//                                                             "Backspace",
//                                                             "Delete",
//                                                             "ArrowLeft",
//                                                             "ArrowRight",
//                                                             "Tab",
//                                                         ].includes(e.key)
//                                                     ) {
//                                                         e.preventDefault();
//                                                     }
//                                                 }}
//                                                 onChange={(e) => {
//                                                     const value = e.target.value;

//                                                     // Only letters and spaces
//                                                     if (/^[A-Za-z\s]*$/.test(value)) {
//                                                         setFieldValue("city", value);
//                                                     }
//                                                 }}
//                                                 onBlur={() => setFieldTouched("city", true)}
//                                             />
//                                         </FieldWrappers>
//                                         {/* {touched.city && errors.city && (
//                                             <ErrorText>{errors.city}</ErrorText>
//                                         )} */}
//                                     </InputGroup>

//                                     <InputGroup>
//                                         <Label htmlFor="pincode">Pincode *</Label>
//                                         <FieldWrappers
//                                             className={getErrorBorder(
//                                                 touched.pincode,
//                                                 errors.pincode
//                                             )}
//                                         >
//                                             <Field
//                                                 id="pincode"
//                                                 name="pincode"
//                                                 type="text"
//                                                 inputMode="numeric"
//                                                 maxLength={6}
//                                                 placeholder="Enter pincode"
//                                                 onKeyDown={(e) => {
//                                                     // Allow only numbers and control keys
//                                                     if (
//                                                         !/[0-9]/.test(e.key) &&
//                                                         ![
//                                                             "Backspace",
//                                                             "Delete",
//                                                             "ArrowLeft",
//                                                             "ArrowRight",
//                                                             "Tab",
//                                                         ].includes(e.key)
//                                                     ) {
//                                                         e.preventDefault();
//                                                     }
//                                                 }}
//                                                 onChange={(e) => {
//                                                     const value = e.target.value;

//                                                     // Only numbers and maximum 6 digits
//                                                     if (/^\d{0,6}$/.test(value)) {
//                                                         setFieldValue("pincode", value);
//                                                     }
//                                                 }}
//                                                 onBlur={() => setFieldTouched("pincode", true)}
//                                             />
//                                         </FieldWrappers>
//                                         {/* {touched.pincode && errors.pincode && (
//                                             <ErrorText>{errors.pincode}</ErrorText>
//                                         )} */}
//                                     </InputGroup>


//                                 </CombineInputGroup>

//                                 <div className="mt-2">
//                                     <InputGroup>
//                                         <Label htmlFor="address">Address *</Label>

//                                         <Field
//                                             as={Textarea}
//                                             id="address"
//                                             name="address"
//                                             rows={3}
//                                             placeholder="Enter address"
//                                         />

//                                         {touched.address && errors.address && (
//                                             <ErrorText>{errors.address}</ErrorText>
//                                         )}
//                                     </InputGroup>
//                                 </div>
//                             </div>

//                             {/* Section 2 */}
//                             <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
//                                 <SectionHeading index="2" title="Employee Details" />

//                                 <CombineInputGroup>
//                                     <InputGroup>
//                                         <Label htmlFor="employeeName">Employee Name *</Label>
//                                         <FieldWrappers
//                                             className={getErrorBorder(
//                                                 touched.employeeName,
//                                                 errors.employeeName
//                                             )}
//                                         >
//                                             <Field
//                                                 id="employeeName"
//                                                 name="employeeName"
//                                                 type="text"
//                                                 placeholder="Enter employee name"
//                                             />
//                                         </FieldWrappers>

//                                         {/* {touched.employeeName && errors.employeeName && (
//                                             <ErrorText>{errors.employeeName}</ErrorText>
//                                         )} */}
//                                     </InputGroup>
//                                     <InputGroup>
//                                         <Label htmlFor="employeeId">Employee ID *</Label>
//                                         <FieldWrappers
//                                             className={getErrorBorder(
//                                                 touched.employeeId,
//                                                 errors.employeeId
//                                             )}
//                                         >
//                                             <Field
//                                                 id="employeeId"
//                                                 name="employeeId"
//                                                 placeholder="Enter employee id"
//                                             />
//                                         </FieldWrappers>
//                                         {/* {touched.employeeId && errors.employeeId && (
//                                             <ErrorText>{errors.employeeId}</ErrorText>
//                                         )} */}
//                                     </InputGroup>


//                                 </CombineInputGroup>

//                                 <CombineInputGroup className="mt-2">
//                                     <InputGroup>
//                                         <Label htmlFor="payPeriod">Pay Period *</Label>
//                                         <FieldWrappers
//                                             className={getErrorBorder(
//                                                 touched.payPeriod,
//                                                 errors.payPeriod
//                                             )}
//                                         >
//                                             <DatePicker
//                                                 selected={
//                                                     values.payPeriod
//                                                         ? new Date(`${values.payPeriod}-01`)
//                                                         : null
//                                                 }
//                                                 onChange={(date) => {
//                                                     if (!date) return;

//                                                     setFieldValue(
//                                                         "payPeriod",
//                                                         moment(date).format("YYYY-MM")
//                                                     );

//                                                     setPayPeriodCalendarOpen(false);
//                                                 }}
//                                                 onClickOutside={() => setPayPeriodCalendarOpen(false)}
//                                                 open={payPeriodCalendarOpen}
//                                                 onInputClick={() => setPayPeriodCalendarOpen(true)}
//                                                 dateFormat="MMMM yyyy"
//                                                 showMonthYearPicker
//                                                 // showFullMonthYearPicker
//                                                 showTwoColumnMonthYearPicker
//                                                 placeholderText="Select pay period"
//                                                 className="w-full cursor-pointer bg-transparent outline-none"
//                                                 showIcon
//                                                 icon={
//                                                     <FaRegCalendar className="mt-0.5 text-xs text-gray-400" />
//                                                 }
//                                                 showPopperArrow={false}
//                                                 onFocus={(e) => e.target.blur()}
//                                                 wrapperClassName="w-full"
//                                                 portalId="root-portal"
//                                                 popperClassName="react-datepicker-popper"
//                                             />
//                                         </FieldWrappers>

//                                         {/* {touched.payPeriod && errors.payPeriod && (
//                                             <ErrorText>{errors.payPeriod}</ErrorText>
//                                         )} */}
//                                     </InputGroup>
//                                     <InputGroup>
//                                         <Label htmlFor="paidDays">Paid Days *</Label>

//                                         <FieldWrappers
//                                             className={getErrorBorder(
//                                                 touched.paidDays,
//                                                 errors.paidDays
//                                             )}
//                                         >
//                                             <Field
//                                                 id="paidDays"
//                                                 name="paidDays"
//                                                 type="text"
//                                                 inputMode="numeric"
//                                                 maxLength={2}
//                                                 placeholder="Enter paid days"
//                                                 onKeyDown={(e) => {
//                                                     // Allow only numbers and control keys
//                                                     if (
//                                                         !/[0-9]/.test(e.key) &&
//                                                         ![
//                                                             "Backspace",
//                                                             "Delete",
//                                                             "ArrowLeft",
//                                                             "ArrowRight",
//                                                             "Tab",
//                                                         ].includes(e.key)
//                                                     ) {
//                                                         e.preventDefault();
//                                                     }
//                                                 }}
//                                                 onChange={(e) => {
//                                                     const value = e.target.value;

//                                                     // Only numbers
//                                                     if (!/^\d*$/.test(value)) {
//                                                         return;
//                                                     }

//                                                     // Maximum 31
//                                                     if (value === "" || Number(value) <= 31) {
//                                                         setFieldValue("paidDays", value);
//                                                     }
//                                                 }}
//                                                 onBlur={() => setFieldTouched("paidDays", true)}
//                                             />
//                                         </FieldWrappers>

//                                         {/* {touched.paidDays && errors.paidDays && (
//                                             <ErrorText>{errors.paidDays}</ErrorText>
//                                         )} */}
//                                     </InputGroup>


//                                 </CombineInputGroup>

//                                 <CombineInputGroup className="mt-2">
//                                     <InputGroup>
//                                         <Label htmlFor="lossOfPayDays">Loss of Pay Days *</Label>

//                                         <FieldWrappers
//                                             className={getErrorBorder(
//                                                 touched.lossOfPayDays,
//                                                 errors.lossOfPayDays
//                                             )}
//                                         >
//                                             <Field
//                                                 id="lossOfPayDays"
//                                                 name="lossOfPayDays"
//                                                 type="text"
//                                                 inputMode="numeric"
//                                                 maxLength={2}
//                                                 placeholder="Enter loss of pay days"
//                                                 onKeyDown={(e) => {
//                                                     // Allow only numbers and control keys
//                                                     if (
//                                                         !/[0-9]/.test(e.key) &&
//                                                         ![
//                                                             "Backspace",
//                                                             "Delete",
//                                                             "ArrowLeft",
//                                                             "ArrowRight",
//                                                             "Tab",
//                                                         ].includes(e.key)
//                                                     ) {
//                                                         e.preventDefault();
//                                                     }
//                                                 }}
//                                                 onChange={(e) => {
//                                                     const value = e.target.value;

//                                                     // Only numbers
//                                                     if (!/^\d*$/.test(value)) {
//                                                         return;
//                                                     }

//                                                     // Maximum 31
//                                                     if (value === "" || Number(value) <= 31) {
//                                                         setFieldValue("lossOfPayDays", value);
//                                                     }
//                                                 }}
//                                                 onBlur={() => setFieldTouched("lossOfPayDays", true)}
//                                             />
//                                         </FieldWrappers>

//                                         {/* {touched.lossOfPayDays && errors.lossOfPayDays && (
//                                             <ErrorText>{errors.lossOfPayDays}</ErrorText>
//                                         )} */}
//                                     </InputGroup>
//                                     <InputGroup>
//                                         <Label htmlFor="payDate">Pay Date *</Label>

//                                         <FieldWrappers
//                                             className="relative cursor-pointer items-center h-11"
//                                         >
//                                             <DatePicker
//                                                 selected={
//                                                     values.payDate
//                                                         ? new Date(values.payDate)
//                                                         : null
//                                                 }
//                                                 onChange={(date) => {
//                                                     if (!date) return;

//                                                     setFieldValue(
//                                                         "payDate",
//                                                         moment(date).format("YYYY-MM-DD")
//                                                     );

//                                                     setPayDateCalendarOpen(false);
//                                                 }}
//                                                 onClickOutside={() =>
//                                                     setPayDateCalendarOpen(false)
//                                                 }
//                                                 onSelect={() =>
//                                                     setPayDateCalendarOpen(false)
//                                                 }
//                                                 open={payDateCalendarOpen}
//                                                 onInputClick={() =>
//                                                     setPayDateCalendarOpen(true)
//                                                 }
//                                                 dateFormat="yyyy-MM-dd"
//                                                 placeholderText="Select pay date"
//                                                 className="w-full cursor-pointer bg-transparent outline-none"
//                                                 showIcon
//                                                 icon={
//                                                     <FaRegCalendar className="mt-0.5 text-xs text-gray-400" />
//                                                 }
//                                                 showPopperArrow={false}
//                                                 onFocus={(e) => e.target.blur()}
//                                                 wrapperClassName="w-full"
//                                                 portalId="root-portal"
//                                                 popperClassName="react-datepicker-popper"
//                                             />
//                                         </FieldWrappers>

//                                         {touched.payDate && errors.payDate && (
//                                             <ErrorText>
//                                                 {errors.payDate}
//                                             </ErrorText>
//                                         )}
//                                     </InputGroup>


//                                 </CombineInputGroup>
//                             </div>

//                             {/* Section 3 */}
//                             {/* <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
//                                 <SectionHeading index="3" title="Income Details" />
                                
//                             </div> */}

//                             {/* ===================== SECTION 3 ===================== */}
//                             <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
//                                 <SectionHeading index="3" title="Income & Deductions" />

//                                 <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

//                                     {/* ===================== EARNINGS ===================== */}
//                                     <div>
//                                         <div className="mb-3">
//                                             <h3 className="text-sm font-semibold text-slate-800">
//                                                 Income
//                                             </h3>
//                                             <p className="mt-0.5 text-xs text-slate-500">
//                                                 Salary components paid to the employee
//                                             </p>
//                                         </div>

//                                         <div className="overflow-hidden rounded-lg border border-slate-200">
//                                             <table className="w-full text-sm">
//                                                 <thead className="bg-slate-50">
//                                                     <tr>
//                                                         <th className="p-3 text-left font-semibold text-slate-600">
//                                                             Income
//                                                         </th>

//                                                         <th className="w-36 p-3 text-right font-semibold text-slate-600">
//                                                             Amount
//                                                         </th>
//                                                     </tr>
//                                                 </thead>

//                                                 <tbody className="divide-y divide-slate-200">
//                                                     {earnings.map((item, index) => (
//                                                         <tr key={index}>
//                                                             {/* <td className="p-3">
//                                                                 <div className="px-1 py-2 text-sm font-medium text-slate-700">
//                                                                     {item.description}
//                                                                 </div>
//                                                             </td> */}
//                                                             <td className="p-3">
//                                                                 {index === 2 ? (
//                                                                     <FieldWrappers>
//                                                                         <input
//                                                                             type="text"
//                                                                             className="w-full bg-transparent outline-none"
//                                                                             placeholder="Enter income name"
//                                                                             value={item.description}
//                                                                             onChange={(e) => {
//                                                                                 const description = e.target.value;

//                                                                                 updateEarning(index, "description", description);

//                                                                                 // Clear amount when description is removed
//                                                                                 if (!description.trim()) {
//                                                                                     updateEarning(index, "amount", "");
//                                                                                 }
//                                                                             }}
//                                                                         />
//                                                                     </FieldWrappers>
//                                                                 ) : (
//                                                                     <div className="px-1 py-2 text-sm font-medium text-slate-700">
//                                                                         {item.description}
//                                                                     </div>
//                                                                 )}
//                                                             </td>

//                                                             <td className="p-3">
//                                                                 <FieldWrappers
//                                                                     className={`${index === 2 && !item.description.trim()
//                                                                             ? "bg-gray-100 opacity-60 cursor-not-allowed"
//                                                                             : ""
//                                                                         }`}
//                                                                 >
//                                                                     <input
//                                                                         type="text"
//                                                                         inputMode="numeric"
//                                                                         className={`w-full bg-transparent text-left outline-none ${index === 2 && !item.description.trim()
//                                                                                 ? "cursor-not-allowed text-gray-400"
//                                                                                 : ""
//                                                                             }`}
//                                                                         placeholder="Amount"
//                                                                         value={fmtInput(item.amount)}
//                                                                         disabled={index === 2 && !item.description.trim()}
//                                                                         onChange={(e) => {
//                                                                             const rawValue = e.target.value.replace(/,/g, "");

//                                                                             if (rawValue === "" || /^\d+$/.test(rawValue)) {
//                                                                                 const amount = Number(rawValue);

//                                                                                 if (amount <= 1000000 || rawValue === "") {
//                                                                                     updateEarning(
//                                                                                         index,
//                                                                                         "amount",
//                                                                                         rawValue
//                                                                                     );
//                                                                                 } else {
//                                                                                     toast.error(
//                                                                                         "Amount cannot be greater than ₹10,00,000"
//                                                                                     );
//                                                                                 }
//                                                                             }
//                                                                         }}
//                                                                     />
//                                                                 </FieldWrappers>
//                                                             </td>
//                                                         </tr>
//                                                     ))}
//                                                 </tbody>
//                                             </table>
//                                         </div>

//                                         {/* Total Earnings */}
//                                         <div className="mt-3 flex justify-end">
//                                             <div className="flex w-52 justify-between border-t pt-3 text-sm font-semibold">
//                                                 <span>Total Income</span>

//                                                 <span>
//                                                     ₹ {fmt(computedTotals.grossEarnings)}
//                                                 </span>
//                                             </div>
//                                         </div>
//                                     </div>


//                                     {/* ===================== DEDUCTIONS ===================== */}
//                                     <div>
//                                         <div className="mb-3">
//                                             <h3 className="text-sm font-semibold text-slate-800">
//                                                 Deductions
//                                             </h3>

//                                             <p className="mt-0.5 text-xs text-slate-500">
//                                                 Applicable deductions from salary
//                                             </p>
//                                         </div>

//                                         <div className="overflow-hidden rounded-lg border border-slate-200">
//                                             <table className="w-full text-sm">
//                                                 <thead className="bg-slate-50">
//                                                     <tr>
//                                                         <th className="p-3 text-left font-semibold text-slate-600">
//                                                             Deductions
//                                                         </th>

//                                                         <th className="w-36 p-3 text-right font-semibold text-slate-600">
//                                                             Amount
//                                                         </th>
//                                                     </tr>
//                                                 </thead>

//                                                 <tbody className="divide-y divide-slate-200">
//                                                     {deductions.map((item, index) => (
//                                                         <tr key={index}>
//                                                             {/* <td className="p-3">
//                                                                 <div className="px-1 py-2 text-sm font-medium text-slate-700">
//                                                                     {item.description}
//                                                                 </div>
//                                                             </td> */}
//                                                             <td className="p-3">
//                                                                 {index === 2 ? (
//                                                                     <FieldWrappers>
//                                                                         <input
//                                                                             type="text"
//                                                                             className="w-full bg-transparent outline-none"
//                                                                             placeholder="Enter deduction name"
//                                                                             value={item.description}
//                                                                             onChange={(e) => {
//                                                                                 const description = e.target.value;

//                                                                                 updateDeduction(
//                                                                                     index,
//                                                                                     "description",
//                                                                                     description
//                                                                                 );

//                                                                                 if (!description.trim()) {
//                                                                                     updateDeduction(index, "amount", "");
//                                                                                 }
//                                                                             }}
//                                                                         />
//                                                                     </FieldWrappers>
//                                                                 ) : (
//                                                                     <div className="px-1 py-2 text-sm font-medium text-slate-700">
//                                                                         {item.description}
//                                                                     </div>
//                                                                 )}
//                                                             </td>
//                                                             <td className="p-3">
//                                                                 <FieldWrappers
//                                                                     className={`${index === 2 && !item.description.trim()
//                                                                             ? "bg-gray-100 opacity-60 cursor-not-allowed"
//                                                                             : ""
//                                                                         }`}
//                                                                 >
//                                                                     <input
//                                                                         type="text"
//                                                                         inputMode="numeric"
//                                                                         className={`w-full bg-transparent text-left outline-none ${index === 2 && !item.description.trim()
//                                                                                 ? "cursor-not-allowed text-gray-400"
//                                                                                 : ""
//                                                                             }`}
//                                                                         placeholder="Amount"
//                                                                         value={fmtInput(item.amount)}
//                                                                         disabled={index === 2 && !item.description.trim()}
//                                                                         onChange={(e) => {
//                                                                             const rawValue = e.target.value.replace(/,/g, "");

//                                                                             if (rawValue === "" || /^\d+$/.test(rawValue)) {
//                                                                                 const amount = Number(rawValue);

//                                                                                 if (amount <= 1000000 || rawValue === "") {
//                                                                                     updateDeduction(
//                                                                                         index,
//                                                                                         "amount",
//                                                                                         rawValue
//                                                                                     );
//                                                                                 } else {
//                                                                                     toast.error(
//                                                                                         "Amount cannot be greater than ₹10,00,000"
//                                                                                     );
//                                                                                 }
//                                                                             }
//                                                                         }}
//                                                                     />
//                                                                 </FieldWrappers>
//                                                             </td>
//                                                         </tr>
//                                                     ))}
//                                                 </tbody>
//                                             </table>
//                                         </div>

//                                         {/* Total Deductions */}
//                                         <div className="mt-3 flex justify-end">
//                                             <div className="flex w-52 justify-between border-t pt-3 text-sm font-semibold">
//                                                 <span>Total Deductions</span>

//                                                 <span>
//                                                     ₹ {fmt(computedTotals.totalDeductions)}
//                                                 </span>
//                                             </div>
//                                         </div>
//                                     </div>

//                                 </div>


//                                 {/* ===================== NET SALARY ===================== */}
//                                 <div className="mt-6 flex justify-end">
//                                     <div className="w-80 rounded-lg bg-slate-50 p-4">

//                                         <div className="flex justify-between text-sm">
//                                             <span className="text-slate-600">
//                                                 Gross Income
//                                             </span>

//                                             <span className="font-medium">
//                                                 ₹ {fmt(computedTotals.grossEarnings)}
//                                             </span>
//                                         </div>

//                                         <div className="mt-2 flex justify-between text-sm">
//                                             <span className="text-slate-600">
//                                                 Total Deductions
//                                             </span>

//                                             <span className="font-medium">
//                                                 ₹ {fmt(computedTotals.totalDeductions)}
//                                             </span>
//                                         </div>

//                                         <div className="mt-3 flex justify-between border-t pt-3 text-lg font-bold text-[#1A365D]">
//                                             <span>Net Salary</span>

//                                             <span>
//                                                 ₹ {fmt(computedTotals.totalNetPayable)}
//                                             </span>
//                                         </div>

//                                         <div className="mt-2 border-t border-dashed pt-2 text-right text-xs text-slate-500">
//                                             {computedTotals.amountInWords}
//                                         </div>

//                                     </div>
//                                 </div>
//                             </div>
//                         </div>

//                         {/* footer action */}
//                         <div className="mt-8 flex justify-end">

//                             <SubmitBtn
//                                 type="submit"
//                                 disabled={isSalarySlipLoading}
//                             >
//                                 {isSalarySlipLoading ? "Generating..." : "Generate Slip"}
//                             </SubmitBtn>
//                         </div>
//                     </div>
//                 </Form>
//             )}
//         </Formik>
//     );
// }

// const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;
// const FieldWrapper = tw.div`w-full`;
// const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
